import React from 'react';
import { SkeletonChartPlot } from '../ReportsNative';

// Base shimmer bar — uses design system onyx tokens
export function SkeletonLoader({ width = '100%', height = '20px', borderRadius = '4px', style = {} }) {
  // `.rn-sk` (src/index.css) carries the surface + sweep so every skeleton
  // on the app shares one loading language with the report/chart kit.
  return <div className="rn-sk" style={{ width, height, borderRadius, ...style }} />;
}

// Card shell — matches the design system Card / KpiTile look
function SkeletonCard({ children, style = {} }) {
  return (
    <div
      style={{
        position: 'relative',
        padding: 'var(--sp-6)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--lodge-white)',
        border: '1px solid var(--lodge-onyx-100)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Skeleton for KPI tiles — mirrors KpiTile structure exactly
export function KpiSkeleton() {
  return (
    <SkeletonCard style={{ padding: '15px 16px 14px', minHeight: 118, boxShadow: 'none' }}>
      {/* Header row: label skeleton */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <SkeletonLoader height="11px" width="72px" borderRadius="3px" />
      </div>

      {/* Large value */}
      <SkeletonLoader height="30px" width="65%" borderRadius="6px" style={{ marginBottom: 10 }} />

      {/* Footer: delta pill + note */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
        <SkeletonLoader height="18px" width="48px" borderRadius="5px" />
        <SkeletonLoader height="11px" width="80px" borderRadius="3px" />
      </div>

      {/* Sub line */}
      <SkeletonLoader height="11px" width="58%" borderRadius="3px" />
    </SkeletonCard>
  );
}

// Skeleton for charts — matches Card component structure. `kind` picks the
// bklit loading family ('area' | 'bar') so the placeholder animates like the
// chart it stands in for.
export function ChartSkeleton({ height = '300px', kind = 'area' }) {
  return (
    <SkeletonCard>
      {/* Card header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, paddingBottom: 16,
        borderBottom: '1px solid var(--lodge-onyx-100)',
      }}>
        <SkeletonLoader height="16px" width="120px" borderRadius="4px" />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <SkeletonLoader height="11px" width="44px" borderRadius="3px" />
          <SkeletonLoader height="22px" width="36px" borderRadius="var(--radius-md)" />
          <SkeletonLoader height="22px" width="50px" borderRadius="var(--radius-md)" />
          <SkeletonLoader height="22px" width="36px" borderRadius="var(--radius-md)" />
        </div>
      </div>
      {/* Plot frame: y-tick gutter + dashed gridlines + x ticks, so the
          gutters land where the real chart's axes will. */}
      <div className="rn-sk-chart" style={{ height, padding: 0 }}>
        <div className="rn-sk-chart-y">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonLoader key={i} height="9px" width={i === 0 ? '30px' : '26px'} borderRadius="3px" />
          ))}
        </div>
        <div className="rn-sk-chart-plot">
          <SkeletonChartPlot kind={kind} />
        </div>
      </div>
      <div className="rn-sk-chart-x" style={{ padding: '10px 0 0 40px' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <SkeletonLoader key={i} height="9px" width="40px" borderRadius="3px" />
        ))}
      </div>
    </SkeletonCard>
  );
}

// Skeleton for tables
export function TableSkeleton({ rows = 5, columns = 7 }) {
  return (
    <SkeletonCard>
      {/* Card header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, paddingBottom: 16,
        borderBottom: '1px solid var(--lodge-onyx-100)',
      }}>
        <SkeletonLoader height="16px" width="160px" borderRadius="4px" />
        <SkeletonLoader height="32px" width="96px" borderRadius="var(--radius-md)" />
      </div>

      {/* Header row */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 12, marginBottom: 12, padding: '0 8px' }}>
        {Array.from({ length: columns }).map((_, i) => (
          <SkeletonLoader key={i} height="11px" width="75%" borderRadius="3px" />
        ))}
      </div>

      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 12,
            padding: '8px',
            borderTop: '1px solid var(--lodge-onyx-50)',
          }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <SkeletonLoader key={colIdx} height="14px" width={colIdx === 0 ? '88%' : '68%'} borderRadius="3px" />
          ))}
        </div>
      ))}
    </SkeletonCard>
  );
}

// Section header skeleton
export function SectionHeaderSkeleton() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
      <div style={{
        width: 40, height: 40,
        borderRadius: 12,
        backgroundColor: 'var(--lodge-orange-50)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <SkeletonLoader height="20px" width="20px" borderRadius="4px" />
      </div>
      <div>
        <SkeletonLoader height="18px" width="180px" borderRadius="4px" style={{ marginBottom: 4 }} />
        <SkeletonLoader height="12px" width="140px" borderRadius="3px" />
      </div>
    </div>
  );
}

// Sidebar skeleton
export function SidebarSkeleton({ isCollapsed = false }) {
  return (
    <div
      style={{
        position: 'fixed',
        left: 16, top: 16,
        height: 'calc(100vh - 2rem)',
        width: isCollapsed ? 80 : 256,
        borderRadius: 16,
        background: 'var(--lodge-white)',
        border: '1px solid var(--lodge-onyx-100)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        zIndex: 40,
        transition: 'width 0.3s ease',
      }}
    >
      {/* Header */}
      <div style={{
        height: 64,
        borderBottom: '1px solid var(--lodge-onyx-100)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        marginBottom: 12,
        flexShrink: 0,
      }}>
        {!isCollapsed ? (
          <>
            <SkeletonLoader height="32px" width="120px" borderRadius="8px" />
            <SkeletonLoader height="32px" width="32px" borderRadius="8px" />
          </>
        ) : (
          <SkeletonLoader height="32px" width="32px" borderRadius="8px" style={{ margin: '0 auto' }} />
        )}
      </div>

      {/* Nav items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonLoader key={i} height="44px" width="100%" borderRadius="8px" />
        ))}
      </div>

      {/* Bottom: org / user row */}
      {!isCollapsed && (
        <div style={{ borderTop: '1px solid var(--lodge-onyx-100)', padding: 12, marginTop: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 8 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 8, flexShrink: 0,
              background: 'var(--lodge-orange-400)',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <SkeletonLoader height="14px" width="120px" borderRadius="4px" style={{ marginBottom: 4 }} />
              <SkeletonLoader height="11px" width="90px" borderRadius="3px" />
            </div>
            <SkeletonLoader height="14px" width="14px" borderRadius="3px" style={{ flexShrink: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}

// Navbar skeleton
export function NavbarSkeleton({ isCollapsed = false }) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 16, right: 16,
        left: isCollapsed ? 112 : 288,
        height: 64,
        borderRadius: 16,
        background: 'var(--lodge-white)',
        border: '1px solid var(--lodge-onyx-100)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        zIndex: 30,
      }}
    >
      <SkeletonLoader height="40px" width="400px" borderRadius="8px" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SkeletonLoader height="36px" width="36px" borderRadius="8px" />
        <SkeletonLoader height="36px" width="36px" borderRadius="8px" />
      </div>
    </div>
  );
}

// Native-parity mobile dashboard skeleton lives with the MobileDashboard kit.
export { MobileDashboardSkeleton } from '../MobileDashboard/MobileDashboardSkeleton';

// Full dashboard skeleton layout
export function DashboardSkeleton() {
  const kpiTileCount = 5;
  const periodLabels = ['Yesterday', '7d', '30d', '60d', '90d', 'MTD', 'Last month'];
  const compareLabels = ['SPLM', 'Same MTD LY', 'Budget', 'Forecast'];
  const tabLabels = ['Revenue', 'Operational', 'Financial'];

  return (
    <div style={{ padding: 'var(--sp-6) var(--sp-2)' }}>
      <div style={{ maxWidth: 1760, margin: '0 auto' }}>

        {/* Page header: title left, AI briefing pill right */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 24, flexWrap: 'wrap', padding: '10px 4px 18px',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <SkeletonLoader height="32px" width="155px" borderRadius="6px" />
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999, backgroundColor: '#ECFDF3',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#12B76A' }} />
                <SkeletonLoader height="11px" width="120px" borderRadius="3px" />
              </div>
            </div>
            <SkeletonLoader height="13px" width="280px" borderRadius="3px" />
          </div>

          {/* AI Daily Briefing pill */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 12,
            padding: '4px 4px 4px 14px',
            background: 'var(--lodge-white)',
            border: '1px solid var(--lodge-onyx-100)',
            borderRadius: 999, boxShadow: 'var(--shadow-xs)',
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8,
              background: 'rgba(255, 122, 0, 0.10)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SkeletonLoader height="14px" width="14px" borderRadius="3px" />
            </div>
            <SkeletonLoader height="10px" width="110px" borderRadius="3px" />
            <SkeletonLoader height="12px" width="180px" borderRadius="3px" />
            <div style={{ padding: '4px 9px', borderRadius: 999, background: '#ECFDF3' }}>
              <SkeletonLoader height="11px" width="80px" borderRadius="3px" />
            </div>
            <div style={{ padding: '4px 9px', borderRadius: 999, background: '#FFFAEB', border: '1px solid #FDE68A' }}>
              <SkeletonLoader height="11px" width="64px" borderRadius="3px" />
            </div>
            <div style={{ padding: '8px 14px', borderRadius: 999, background: 'var(--lodge-onyx-900)' }}>
              <SkeletonLoader height="11px" width="78px" borderRadius="3px" style={{ opacity: 0.5 }} />
            </div>
          </div>
        </div>

        {/* Filter bar (two rows) */}
        <div style={{ marginBottom: 24 }}>
          <div style={{
            background: 'var(--lodge-white)',
            border: '1px solid var(--lodge-onyx-100)',
            borderRadius: 14,
            padding: '10px 12px',
            boxShadow: 'var(--shadow-xs)',
          }}>
            {/* Row 1: save | periods | date pill | spacer | location */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: '#ECFDF3',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SkeletonLoader height="14px" width="14px" borderRadius="3px" />
              </div>
              <div style={{
                display: 'inline-flex', background: 'var(--lodge-onyx-50)',
                borderRadius: 999, padding: 3, gap: 1,
              }}>
                {periodLabels.map((label, i) => (
                  <div key={i} style={{
                    padding: '7px 14px', borderRadius: 999,
                    background: i === 5 ? 'var(--lodge-orange-400)' : 'transparent',
                    boxShadow: i === 5 ? '0 1px 3px rgba(255, 122, 0, 0.30)' : 'none',
                  }}>
                    <SkeletonLoader height="12px" width={`${Math.max(20, label.length * 6)}px`} borderRadius="3px" style={{ opacity: i === 5 ? 0.5 : 1 }} />
                  </div>
                ))}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 12px', borderRadius: 10,
                background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)',
              }}>
                <SkeletonLoader height="13px" width="13px" borderRadius="3px" />
                <SkeletonLoader height="12px" width="150px" borderRadius="3px" />
                <span style={{ fontSize: 11, color: 'var(--lodge-onyx-500)' }}>▼</span>
              </div>
              <div style={{ flex: 1, minWidth: 8 }} />
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 12px', borderRadius: 10, minWidth: 160,
                background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)',
              }}>
                <SkeletonLoader height="13px" width="13px" borderRadius="3px" />
                <SkeletonLoader height="12px" width="110px" borderRadius="3px" />
                <span style={{ fontSize: 11, color: 'var(--lodge-onyx-500)', marginLeft: 'auto' }}>▼</span>
              </div>
            </div>

            {/* Divider */}
            <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px dashed var(--lodge-onyx-100)' }}>
              {/* Row 2: COMPARE label | compare segmented | summary | refresh */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <SkeletonLoader height="10px" width="60px" borderRadius="3px" />
                <div style={{
                  display: 'inline-flex',
                  border: '1px solid var(--lodge-onyx-100)',
                  borderRadius: 10, padding: 3, gap: 1,
                  background: 'var(--lodge-white)',
                }}>
                  {compareLabels.map((label, i) => (
                    <div key={i} style={{
                      padding: i === 0 ? '5px 13px' : '6px 14px',
                      borderRadius: 7,
                      background: i === 0 ? 'rgba(255, 122, 0, 0.10)' : 'transparent',
                      border: i === 0 ? '1px solid rgba(255, 122, 0, 0.30)' : '1px solid transparent',
                    }}>
                      <SkeletonLoader height="12px" width={`${Math.max(28, label.length * 7)}px`} borderRadius="3px" />
                    </div>
                  ))}
                </div>
                <SkeletonLoader height="12px" width="220px" borderRadius="3px" />
                <div style={{ flex: 1, minWidth: 8 }} />
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 10,
                  background: 'var(--lodge-orange-400)',
                  boxShadow: '0 1px 3px rgba(255, 122, 0, 0.30)',
                }}>
                  <SkeletonLoader height="13px" width="13px" borderRadius="3px" style={{ opacity: 0.5 }} />
                  <SkeletonLoader height="12px" width="50px" borderRadius="3px" style={{ opacity: 0.5 }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, margin: '4px 0 24px',
          borderBottom: '1px solid var(--lodge-onyx-100)',
        }}>
          <div style={{ display: 'inline-flex', gap: 2 }}>
            {tabLabels.map((label, i) => (
              <div key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 16px 14px',
                marginBottom: -1,
                borderBottom: i === 0 ? '2px solid var(--lodge-orange-400)' : '2px solid transparent',
              }}>
                <SkeletonLoader height="15px" width="15px" borderRadius="3px" />
                <SkeletonLoader height="13px" width={`${label.length * 8}px`} borderRadius="3px" />
              </div>
            ))}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 10px',
            }}>
              <SkeletonLoader height="14px" width="14px" borderRadius="3px" />
              <SkeletonLoader height="12px" width="55px" borderRadius="3px" />
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 10px',
            }}>
              <SkeletonLoader height="14px" width="14px" borderRadius="3px" />
              <SkeletonLoader height="12px" width="46px" borderRadius="3px" />
            </div>
          </div>
        </div>

        {/* Active tab body — Revenue section by default */}
        <div style={{ marginBottom: 32 }}>
          {/* Joined sheet — KPI tiles, chart row and table share hairlines
              exactly like the loaded dashboard, so nothing shifts. */}
          <div className="rn-sheet" aria-busy="true" aria-label="Loading dashboard">
            <div
              className="rn-joinrow"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
            >
              {Array.from({ length: kpiTileCount }).map((_, i) => (
                <KpiSkeleton key={i} />
              ))}
            </div>
            <div className="rn-chartgrid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
              <ChartSkeleton height="320px" />
              <ChartSkeleton height="320px" />
            </div>
            <TableSkeleton rows={5} columns={8} />
          </div>
        </div>

      </div>
    </div>
  );
}
